/* eslint no-underscore-dangle: ["error", { "allow": ["__typename"] }] */
import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useReducer,
} from 'react';
import { Pager, ImageUnavailable } from '@stitch-fix/mode-react';
import ShoppableOutfit, {
  ShoppableOutfitPlaceholder,
} from '@stitch-fix/mode-react/ShoppableOutfit';
import { useProductDetailsPageContext } from '../ProductDetailsPage/ProductDetailsPageContext/ProductDetailsPageContext';
import { shopPdpPath } from '../../utils/urlUtils';
import SaveBookmark from '../shared/SaveBookmark';
import ModelSize from './ModelSize';
import styles from './ProductImageGallery.module.scss';

// Pager prop generation is broken causing false test failures.
// Temporarily removing props until issue is resolved.
// See https://stitchfix.atlassian.net/browse/KFE-1528 to track progress.
Pager.propTypes = undefined;

/**
 * If there is an outfit, we do not want to include the Style Card image
 * We also want to keep the style card from loading when querying for ways to wear it,
 * because the query takes a while and the first outfit will abruptly replace the
 * stylecard image (which looks strange). This logic is extrapolated for
 * testability
 *
 * @param outfit
 * @param outfitId
 * @param loadingWaysToWearIt
 * @param images
 * @returns images
 */
export const removeStyleCardIfOutfitIsPresent = (
  outfit,
  outfitId,
  loadingWaysToWearIt,
  images,
) => {
  if ((outfit && outfitId) || loadingWaysToWearIt) {
    return images.filter(
      image => !image.main.key.toLowerCase().startsWith('style_card'),
    );
  }

  return images;
};

/**
 * Transforms image shape for consumption by the Pager component. Extrapolated
 * from the component for testing purposes.
 *
 * @param images
 * @param selectedSkuSize
 * @param selectedSkuBusinessLine
 * @returns images in new shape
 */
export const convertImagesForPagerUse = (images, state) => {
  // Temporary query param for testing new transformations in prod
  const params = new URLSearchParams(window.location.search);
  const useNewTransformations =
    !!params.get('pdp-new-image-transforms') &&
    ['footwear', 'personal accessories'].includes(
      state.shopProduct?.productType?.family?.toLowerCase(),
    );

  let cloudinaryTransforms = {};

  if (useNewTransformations) {
    cloudinaryTransforms = {
      ar: '4:5',
      c: 'lfill',
    };
  }

  return images.map((image, i) => {
    // We need to split originalSrc into a baseUrl and mediaUrl to be compatible with Pager
    const match = image.originalSrc?.match(/(http.*upload\/)(.*)/);
    const mediaPath = match?.[2];
    const baseUrl = match?.[1];

    const imagedHashSources = () => {
      return state.gated
        ? {
            sm: 1000,
          }
        : {
            sm: 560,
            md: 655,
            lg: 800,
          };
    };

    const imageHash = {
      main: {
        alt: image.altText,
        key: `${image.category}-${i}`,
        title: image.label,
        mediaPath,
        baseUrl,
        sizes: imagedHashSources(),
      },
    };

    if (image.label?.toLowerCase() === 'flat_detail') {
      imageHash.main.horizontalAlignment = 'left';
    }

    if (image.modelSize && image.group?.toLowerCase() === 'figure_image') {
      imageHash.caption = <ModelSize modelSize={image.modelSize} />;
    }

    if (image.group?.toLowerCase() !== 'figure_image') {
      imageHash.main.transforms = {
        e: 'replace_color:f2f3f4:300:ffffff',
        ...cloudinaryTransforms,
      };
    }

    // don't show save for later button on style card image
    if (image.group?.toLowerCase() === 'style_card' && state.gated) {
      imageHash.hideActionContent = true;
    }

    return imageHash;
  });
};

/**
 * transforms outfit to image components consumable by the ShoppableOutfit.
 * Extrapolated from component for testing.
 *
 * @param outfit
 * @param subSource
 * @returns imageComponents for consumption by ShoppableOutfit
 */
export const extractImagesForShoppableOutfit = ({
  outfit,
  subSource,
  source,
  collectionName,
  queryId,
  outfitId,
  state,
}) => {
  return outfit.items
    .map(item => {
      const shopProductVariant = item?.shopProductVariant;
      const shopProductImage = shopProductVariant?.image;

      if (!shopProductVariant) {
        // Shop Product Catalog may not have imported the variant yet
        return null;
      }

      const urlParams = state.gated
        ? {
            collection: collectionName,
            outfitId,
            recommendedSkuId: shopProductVariant?.sku,
            source,
            subSource,
            queryId,
          }
        : {
            source,
            subSource,
            outfitId: outfit.id,
            collection: collectionName,
            queryId,
          };

      return {
        id: shopProductVariant?.id,
        imageUrl: shopProductImage?.medium,
        altText: shopProductImage?.altText,
        labelText: item?.sticker?.label,
        laydown: item?.laydown,
        url: shopPdpPath(shopProductVariant?.url, urlParams).replace(
          'product',
          state.urlScope,
        ),
      };
    })
    ?.filter(item => !!item?.id);
};

/**
 * If on-figure imagery is present, we want the shoppable-outfit to render
 * after on-figure, before flat images. If no on-figure imagery is present,
 * render the outfit after the flat images.
 *
 * @param images
 * @param shoppableOutfit
 * @returns images with the shoppable outfit at the correct index
 */
export const determineOutfitIndexPlacement = (
  images,
  shoppableOutfit,
  source,
) => {
  const showOutfitFirst = source === 'share';
  const shoppableOutfitIndex = images.findIndex(image => {
    return !image.main.key.toLowerCase().startsWith('figure');
  });

  if (showOutfitFirst) {
    images.splice(0, 0, shoppableOutfit);
  } else if (shoppableOutfitIndex > 0) {
    // Insert Shoppable Outfit before first Laydown
    images.splice(shoppableOutfitIndex, 0, shoppableOutfit);
  } else {
    // Insert Shoppable Outfit at the end of Laydowns
    images.push(shoppableOutfit);
  }

  return images;
};

const ProductImageGallery = () => {
  const [state, dispatch] = useProductDetailsPageContext();

  const selectedVariant = useMemo(
    () => state.selectedVariant,
    [state.selectedVariant],
  );

  const skuId = useMemo(
    () => selectedVariant?.sku || state.fallbackVariant?.sku || -1,
    [selectedVariant, state.fallbackVariant],
  );

  const businessLine = state.clientBusinessLine || '';
  const loading = state.loading;
  const showPlaceholders = state.loading || !state.shopProduct;
  const media = useMemo(() => {
    return showPlaceholders
      ? []
      : selectedVariant?.media || state.fallbackVariant?.media || [];
  }, [showPlaceholders, state.fallbackVariant?.media, selectedVariant?.media]);
  const outfit = state.waysToWearIt?.items?.nodes[0] || null;
  const outfitId = outfit?.id || '';
  const loadingWaysToWearIt = state.waysToWearItLoading || false;
  const source = state.source || '';
  const subSource = 'shoppable-outfit';
  const collectionName = state.collectionName || '';

  const isKidsBusinessLine = businessLine === 'KIDS';
  const isKidsAndKidsFreestyleIsEnabled =
    state.clientFeatureFlags?.['client.global.kids_categories'] &&
    isKidsBusinessLine;
  const canSaveItem =
    state.canSaveItem &&
    (!isKidsBusinessLine || isKidsAndKidsFreestyleIsEnabled);

  const showBookmark = (!state.isStaticView && canSaveItem) || false;

  const isSaved = selectedVariant?.saved || false;
  const shouldShowWaysToWearIt =
    (!state.waysToWearItLoading &&
      state.waysToWearIt &&
      !!state.waysToWearIt.items.nodes.length) ||
    false;
  const queryId = state.queryId || '';
  const urlScope = state.urlScope;

  const [dotRerenderCount, setDotRerenderCount] = useState(0);
  const [, animateDots] = useReducer(() => {
    setDotRerenderCount(dotRerenderCount + 1);
  });
  const [triggerDotsAnimation, setTriggerDotsAnimation] = useState(false);

  const onJumpLinkClick = useCallback(() => {
    const scrollToWtwiCarousel = () => {
      const waysToWearItCarousel = document.getElementById(
        'ways-to-wear-it-carousel',
      );
      const header = document.querySelector('Header');
      const elementPosition = waysToWearItCarousel?.getBoundingClientRect().top;

      if (!header || !elementPosition) {
        return null;
      }

      const headerOffset = header.offsetHeight / 2;
      const offsetPosition =
        elementPosition - headerOffset + window.pageYOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });

      return true;
    };

    scrollToWtwiCarousel();
  }, []);

  useEffect(() => {
    if (triggerDotsAnimation) {
      animateDots();
    }
  }, [triggerDotsAnimation]);

  let images = useMemo(() => [...media], [media]);

  if (images[0]?.position !== undefined) {
    images.sort((image1, image2) =>
      image1.position < image2.position ? -1 : 1,
    );
  }

  images = convertImagesForPagerUse(images, state);

  images = removeStyleCardIfOutfitIsPresent(
    outfit,
    outfitId,
    loadingWaysToWearIt,
    images,
  );

  const outfitImages = useMemo(() => {
    if (!outfitId || !outfit) return null;

    return extractImagesForShoppableOutfit({
      outfit,
      outfitId,
      subSource,
      source,
      collectionName: state.collectionName,
      queryId,
      state,
    });
  }, [outfit, outfitId, subSource, source, queryId, state]);

  const outfitImagesWithoutLabel = useMemo(() => {
    if (!outfitId || !outfit) return null;

    return outfit.items.map(item => {
      const shopProductVariant = item?.shopProductVariant;
      const shopProductImage = shopProductVariant?.image;

      return {
        id: shopProductVariant?.id,
        imageUrl: shopProductImage?.medium,
        altText: shopProductImage?.altText,
        laydown: item.laydown,
        url: shopPdpPath(shopProductVariant?.url, {
          collection: collectionName,
          outfitId,
          recommendedSkuId: shopProductVariant?.sku,
          source,
          subSource,
        }).replace('product', urlScope),
      };
    });
  }, [outfit, outfitId, collectionName, source, subSource, urlScope]);

  if (outfitId && outfit) {
    const renderPagerLargeOutfit = () => {
      if (loading) return <ShoppableOutfitPlaceholder />;

      const shoppableOutfit = (
        <ShoppableOutfit
          outfitId={outfitId}
          imageComponents={outfitImages}
          dotRerenderCount={dotRerenderCount}
          eventOverrides={{ view: { name: 'pager_large_outfit' } }}
        />
      );

      const wrappedOutfit = state.isStaticView ? (
        <div className={styles.arrow}>
          <div className={styles.blockClicks}>{shoppableOutfit}</div>
        </div>
      ) : (
        shoppableOutfit
      );

      return <div key="your_look">{wrappedOutfit}</div>;
    };

    const renderThumbnailOutfit = () =>
      loading ? (
        <ShoppableOutfitPlaceholder />
      ) : (
        <ShoppableOutfit
          outfitId={outfitId}
          imageComponents={outfitImagesWithoutLabel}
          dotRerenderCount={dotRerenderCount}
        />
      );

    const shoppableOutfitPagerSlide = {
      main: renderPagerLargeOutfit(),
      // don't show save for later button on shoppable outfit
      hideActionContent: true,
      // thumbnail does not display the 'Your Item' label
      thumbnail: renderThumbnailOutfit(),
    };

    // Product Details Service returns On-Figures images first
    images = determineOutfitIndexPlacement(
      images,
      shoppableOutfitPagerSlide,
      source,
    );
  }

  if (!loading && images.length === 0) {
    images = [
      {
        main: <ImageUnavailable />,
        hideActionContent: true,
      },
    ];
  }

  const onSlideChange = useCallback(
    slideIndex => {
      if (!state.gated) {
        dispatch({ type: 'CLEAR_TOOLTIPS' });
      }
      setTriggerDotsAnimation(
        images[slideIndex]?.key === 'your_look' && state.gated,
      );
    },
    [state.gated, images, dispatch],
  );

  return (
    <div data-testid="pager-component">
      <Pager
        slides={loading ? [] : images}
        onSlideChange={onSlideChange}
        jumpLink={
          shouldShowWaysToWearIt
            ? {
                text: 'See more ways to wear it',
                onClick: onJumpLinkClick,
              }
            : null
        }
        actionContent={
          state.gated &&
          showBookmark && (
            <SaveBookmark
              isSaved={isSaved}
              outfitId={outfitId}
              canonicalName="pdp"
              skuId={skuId}
            />
          )
        }
      />
    </div>
  );
};

export default ProductImageGallery;
